import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, PenLine, CheckCircle2, CreditCard, ArrowRight } from "lucide-react";
import { Portfolio } from "@/components/Portfolio";
import { Seo } from "@/components/Seo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/abd4f8f5-ed5f-494d-9745-ce8734069e02/images/837fa8c1f97f646e39c0964e812782fdd7c988407835db117237a3ae2d52fc8e.jpeg";
const PRODUCTION = "https://static.prod-images.emergentagent.com/jobs/abd4f8f5-ed5f-494d-9745-ce8734069e02/images/c9c494d2da80836d701c4fd0662c2dae01ca77f7860457c5c7a44fb978b90362.jpeg";

const STEPS = [
  { icon: PenLine, title: "Fikrini Yaz", desc: "Kafandaki hikâyeyi birkaç cümleyle anlat, gerisini biz halledelim." },
  { icon: Sparkles, title: "AI Senaryo", desc: "Yapay zeka fikrinden sinematik bir taslak senaryo üretir." },
  { icon: CheckCircle2, title: "Düzenle & Onayla", desc: "Senaryoyu istediğin gibi revize et ve onayına sun." },
  { icon: CreditCard, title: "Teklif & Ön Ödeme", desc: "Net maliyet teklifini al, %50 ön ödemeyle prodüksiyonu başlat." },
];

export default function Landing() {
  const [c, setC] = useState({});
  useEffect(() => {
    api.get("/content").then((r) => setC(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    if (c.seo_title) document.title = c.seo_title;
    if (c.seo_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.setAttribute("content", c.seo_description);
    }
  }, [c]);
  const heroImg = c.hero_image || HERO;
  const ctaImg = c.cta_image || PRODUCTION;
  return (
    <div>
      <Seo title={c.seo_home_title || c.seo_title} description={c.seo_home_description || c.seo_description}
        keywords={c.seo_home_keywords} ogImage={c.seo_home_og} />
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={heroImg} alt="Sinematik kamera" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-gold/10 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-sm uppercase tracking-[0.3em] text-gold mb-6">{c.hero_eyebrow || "Prodüksiyon Müşteri Portalı"}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6">
              {c.hero_title || "Fikirden perdeye tek bir platform."}
            </h1>
            <p className="text-lg text-white/70 mb-10 leading-relaxed max-w-xl">
              {c.hero_subtitle || "İnce Fikirler AI Studio ile senaryonu yapay zekayla oluştur, onayla, teklifini al ve ön ödemeni yaparak prodüksiyonunu başlat. Tüm süreç tek ekranda."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="bg-gold text-black hover:bg-gold-light rounded-md px-8 h-12 text-base">
                  Hemen Başla <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-btn">
                <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/5 rounded-md px-8 h-12 text-base">
                  Giriş Yap
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <p className="text-sm uppercase tracking-[0.3em] text-gold mb-3">Nasıl Çalışır</p>
        <h2 className="font-serif text-4xl lg:text-5xl mb-16 max-w-xl">Dört adımda fikrinden filme.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#0A0A0A] border border-white/5 rounded-xl p-7 hover:border-gold/30 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-gold" />
              </div>
              <span className="text-xs text-white/30">0{i + 1}</span>
              <h3 className="font-serif text-2xl mt-1 mb-2">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio */}
      <Portfolio />

      {/* CTA band */}
      <section className="relative py-28 overflow-hidden border-t border-white/5">
        <img src={ctaImg} alt="Klaket" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl mb-6">{c.cta_title || "Hikâyeni anlatmaya hazır mısın?"}</h2>
          <p className="text-white/60 mb-10">{c.cta_subtitle || "Ücretsiz hesap oluştur, ilk fikrini yaz ve AI senaryonu birkaç saniyede al."}</p>
          <Link to="/register" data-testid="cta-register-btn">
            <Button className="bg-gold text-black hover:bg-gold-light rounded-md px-10 h-12 text-base">
              Ücretsiz Başla
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
